const maxApiKeyExpiryDays = 3650;

export const apiKeyExpiryDayOptions = [1, 7, 30, 90, 180, 365] as const;

export function normalizeApiKeyExpiryDays(value: unknown, fallback: number | null = null) {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  const numericValue = typeof value === "number" ? value : Number.parseInt(String(value), 10);

  if (!Number.isFinite(numericValue) || !Number.isInteger(numericValue)) {
    return fallback;
  }

  if (numericValue <= 0 || numericValue > maxApiKeyExpiryDays) {
    return fallback;
  }

  return numericValue;
}

export function formatApiKeyExpiryDaysLabel(days: number) {
  if (days === 1) {
    return "1 day";
  }

  if (days % 365 === 0) {
    const years = days / 365;
    return `${years} year${years === 1 ? "" : "s"}`;
  }

  return `${days} days`;
}

export function addDaysToIsoTimestamp(timestamp: string, days: number) {
  const normalizedDays = normalizeApiKeyExpiryDays(days, null);

  if (normalizedDays === null) {
    return null;
  }

  const parsedTimestamp = Date.parse(timestamp);

  if (!Number.isFinite(parsedTimestamp)) {
    return null;
  }

  return new Date(parsedTimestamp + normalizedDays * 24 * 60 * 60 * 1000).toISOString();
}

function parseTimestamp(value: string | null | undefined) {
  if (!value) {
    return Number.NaN;
  }

  return Date.parse(value);
}

export function resolveApiKeyEffectiveExpiry({
  createdAt,
  tokenExpiresAt,
  workspaceExpiryDays,
}: {
  createdAt: string;
  tokenExpiresAt: string | null | undefined;
  workspaceExpiryDays: number | null | undefined;
}) {
  const candidates = [tokenExpiresAt ?? null];

  if (workspaceExpiryDays !== null && workspaceExpiryDays !== undefined) {
    candidates.push(addDaysToIsoTimestamp(createdAt, workspaceExpiryDays));
  }

  const timestamps = candidates
    .map(value => ({ value, timestamp: parseTimestamp(value) }))
    .filter((entry): entry is { value: string; timestamp: number } => Boolean(entry.value) && Number.isFinite(entry.timestamp));

  if (timestamps.length === 0) {
    return null;
  }

  timestamps.sort((left, right) => left.timestamp - right.timestamp);
  return timestamps[0]?.value ?? null;
}

export function isApiKeyExpired(expiresAt: string | null | undefined, now = Date.now()) {
  const parsedExpiry = parseTimestamp(expiresAt);
  return Number.isFinite(parsedExpiry) && parsedExpiry <= now;
}